/**
 * Safe Fetch Utility (Story 4.2)
 *
 * Provides standardized HTTP error coercion and token sanitization for all adapter
 * HTTP calls. Adapters import safeFetch() instead of calling fetch() directly.
 *
 * Security: All error messages pass through sanitizeErrorMessage() before reaching
 * AdapterError.message or database storage. Provider error responses often contain
 * tokens in URLs — these MUST be sanitized.
 *
 * Timeout: Uses AbortController with 25s default (leaving 5s headroom within
 * Cloudflare Workers' 30s CPU limit).
 */
import type { AdapterError, AdapterErrorCode, AdapterResult } from "./adapter";
import { adapterErr, adapterOk } from "./adapter";

/** Default timeout in milliseconds (25s — leaves 5s headroom for Workers' 30s limit) */
const DEFAULT_TIMEOUT_MS = 25_000;

/**
 * Strips sensitive tokens from error messages before they reach AdapterError or database storage.
 *
 * Removes:
 * - URL query params: access_token=..., token=..., key=...
 * - Bearer token prefixes: "Bearer ey..." or "Bearer ya29..."
 * - Long base64-like strings (40+ alphanumeric/+/= chars) that look like tokens
 *
 * @param message - Raw error message that may contain tokens
 * @returns Sanitized message safe for logging and storage
 */
export function sanitizeErrorMessage(message: string): string {
  let sanitized = message;

  // Strip access_token=, token=, key= URL query parameters
  // Uses [^&]* (not [^&\s]*) to match tokens that contain newlines or whitespace
  sanitized = sanitized.replace(
    /([?&])(access_token|token|key|api_key|client_secret)=[^&]*/gi,
    "$1$2=[REDACTED]"
  );

  // Strip Bearer token values — both literal "Bearer " and URL-encoded "Bearer%20"
  sanitized = sanitized.replace(
    /Bearer(?:\s+|%20)[A-Za-z0-9\-_.~+/]+=*/gi,
    "Bearer [REDACTED]"
  );

  // Strip long base64-like strings (40+ chars that look like tokens/keys)
  sanitized = sanitized.replace(
    /[A-Za-z0-9+/]{40,}={0,3}/g,
    "[REDACTED_TOKEN]"
  );

  return sanitized;
}

/**
 * Maps HTTP status codes to AdapterErrorCode.
 */
function httpStatusToErrorCode(status: number): AdapterErrorCode {
  if (status === 401) return "token_expired";
  if (status === 403) return "permission_denied";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "provider_unavailable";
  return "invalid_response";
}

/**
 * Determines if an error is retryable based on its error code.
 */
function isRetryable(code: AdapterErrorCode): boolean {
  return code === "rate_limited" || code === "provider_unavailable" || code === "network_error";
}

/**
 * Parses Retry-After header value to milliseconds.
 * Supports both seconds (integer) and HTTP-date formats.
 * Returns undefined if the header is missing or unparseable.
 */
function parseRetryAfter(headerValue: string | null): number | undefined {
  if (!headerValue) return undefined;

  // Try as integer seconds first
  const seconds = Number.parseInt(headerValue, 10);
  if (!Number.isNaN(seconds) && seconds > 0) {
    return seconds * 1000;
  }

  // Try as HTTP-date
  const date = new Date(headerValue);
  if (!Number.isNaN(date.getTime())) {
    const ms = date.getTime() - Date.now();
    return ms > 0 ? ms : undefined;
  }

  return undefined;
}

/**
 * Extracts and sanitizes a response body excerpt for providerMessage.
 * Limits to 500 chars to prevent huge payloads in error objects.
 */
async function extractProviderMessage(response: Response): Promise<string | undefined> {
  try {
    const text = await response.text();
    if (!text) return undefined;
    // Sanitize BEFORE truncating to prevent partial token leaks at the 500-char boundary.
    // A token spanning the cut point would be split, leaving a partial prefix too short
    // to match the 40+ char regex pattern.
    const sanitized = sanitizeErrorMessage(text);
    return sanitized.length > 500 ? `${sanitized.slice(0, 500)}...` : sanitized;
  } catch {
    return undefined;
  }
}

/**
 * Standardized fetch wrapper for all adapter HTTP calls.
 *
 * Features:
 * - AbortController timeout (default 25s)
 * - HTTP error coercion: 401 → token_expired, 403 → permission_denied,
 *   429 → rate_limited, 5xx → provider_unavailable
 * - Token sanitization on all error messages
 * - Retry-After header parsing for 429 responses
 * - Provider error context preservation (sanitized response body)
 *
 * @param url - The URL to fetch
 * @param init - Standard RequestInit options
 * @param timeoutMs - Timeout in milliseconds (default: 25000)
 * @returns AdapterResult<Response> on success, AdapterResult with AdapterError on failure
 */
/**
 * Safely extracts hostname from a URL string.
 * Returns the URL string itself (sanitized) if parsing fails.
 */
function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return sanitizeErrorMessage(url);
  }
}

export async function safeFetch(
  url: string,
  init?: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<AdapterResult<Response>> {
  // Extract hostname before fetch to avoid new URL() throwing in the catch block
  const hostname = safeHostname(url);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...init,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      return adapterOk(response);
    }

    // Non-OK response — map to AdapterError
    const code = httpStatusToErrorCode(response.status);
    const retryAfterMs = code === "rate_limited"
      ? parseRetryAfter(response.headers.get("Retry-After"))
      : undefined;
    const providerMessage = await extractProviderMessage(response);

    const error: AdapterError = {
      code,
      message: sanitizeErrorMessage(
        `HTTP ${response.status} from ${hostname}: ${response.statusText}`
      ),
      retryable: isRetryable(code),
      retryAfterMs,
      providerStatus: response.status,
      providerMessage,
    };

    return adapterErr(error);
  } catch (err) {
    clearTimeout(timeoutId);

    // AbortController timeout
    if (err instanceof DOMException && err.name === "AbortError") {
      return adapterErr({
        code: "network_error",
        message: sanitizeErrorMessage(`Request to ${hostname} timed out after ${timeoutMs}ms`),
        retryable: true,
      });
    }

    // Network errors (DNS, connection refused, etc.)
    const errorMessage = err instanceof Error ? err.message : String(err);
    return adapterErr({
      code: "network_error",
      message: sanitizeErrorMessage(`Network error: ${errorMessage}`),
      retryable: true,
    });
  }
}
